// Prototype runtime: plays a file's interactions (navigation, overlays, interactive components,
// variables and conditionals) against a private copy of the document, so presenting never edits
// the real file. The app's Present mode and agents' headless simulation share this code.

import { Document } from './document.ts';
import { Editor } from './editor.ts';
import { hitTestPath } from './hittest.ts';
import { componentSetOf, swapInstance } from './variants.ts';
import { resolveVariable } from './tokens.ts';
import type { FlowStart, MediaAction, NodeData, NodeId, NodeProps, Op, Paint, PrototypeAction, Reaction, Transition, VariableValue, Vec2 } from './types.ts';
import { indexBetween } from './util.ts';

export type PlayerEvent =
  | { type: 'navigate'; id: NodeId; transition: Transition; duration: number; back?: boolean }
  | { type: 'overlay'; id: NodeId | null }
  | { type: 'url'; url: string }
  /** the shown document changed (variant switch, variable update) */
  | { type: 'change' }
  /** a video layer's playback changed: play/pause, sound, or a seek (seconds, absolute or relative) */
  | { type: 'media'; id: NodeId; playing: boolean; muted: boolean; seekTo?: number; seekBy?: number };

/** A video layer's playback in a prototype. */
export interface MediaState {
  playing: boolean;
  muted: boolean;
}

/** Whether a layer has a video fill. */
export function hasVideo(n: NodeData | undefined): boolean {
  return !!n?.fills.some((p) => p.type === 'IMAGE' && p.video && p.visible !== false);
}

/** How a video starts out: Figma's defaults are autoplay, loop and muted. */
export function videoPlaybackOf(n: NodeData | undefined) {
  return { autoplay: n?.videoPlayback?.autoplay ?? true, loop: n?.videoPlayback?.loop ?? true, muted: n?.videoPlayback?.muted ?? true, showControls: !!n?.videoPlayback?.showControls };
}

export interface FireResult {
  events: PlayerEvent[];
  /** For ON_HOVER / ON_PRESS: undoes the temporary changes when the pointer leaves / lifts. */
  revert?: () => PlayerEvent[];
}

export class PrototypePlayer {
  editor!: Editor;
  stack: NodeId[];
  overlay: NodeId | null = null;
  /** Variable values set while playing; they survive reloads of the source document. */
  vars = new Map<NodeId, VariableValue>();
  /** How far each scrolling frame is scrolled (kept across reloads). */
  scrollOffsets = new Map<NodeId, Vec2>();
  /** Playback of videos the prototype has controlled (others play as their settings say). */
  media = new Map<NodeId, MediaState>();
  /** How each screen on the stack was reached, so Back plays it in reverse. */
  private arrivals: { transition: Transition; duration: number }[] = [];

  constructor(source: Document, startId: NodeId) {
    this.stack = [startId];
    this.load(source);
  }

  get doc(): Document {
    return this.editor.doc;
  }
  get current(): NodeId {
    return this.stack[this.stack.length - 1];
  }
  /** The frame receiving input: the overlay if one is open, else the current screen. */
  get screen(): NodeId {
    return this.overlay ?? this.current;
  }

  /** (Re)build the private copy from the source document, keeping navigation and variables. */
  load(source: Document) {
    this.editor = new Editor(Document.fromJSON(structuredClone(source.toJSON())));
    if (this.vars.size) this.editor.transact('Prototype variables', () => this.vars.forEach((v, id) => this.writeVar(id, v)));
    this.stack = this.stack.filter((id) => this.doc.has(id));
    if (this.overlay && !this.doc.has(this.overlay)) this.overlay = null;
    // the fresh copy has unscrolled positions: shift scrolled content again
    const saved = [...this.scrollOffsets];
    this.scrollOffsets.clear();
    for (const [id, o] of saved) if (this.doc.has(id)) this.scrollBy(id, o.x, o.y);
  }

  /** The innermost scrolling frame under a page point. */
  scrollableAt(point: Vec2): NodeId | null {
    const page = this.doc.pageOf(this.screen);
    if (!page) return null;
    const path = hitTestPath(this.doc, page.id, point, { includeLocked: true });
    for (let i = path.length - 1; i >= 0; i--) {
      const n = path[i];
      if (n.overflowScroll && (n.id === this.screen || this.doc.isAncestor(this.screen, n.id))) return n.id;
    }
    const s = this.doc.get(this.screen);
    return s?.overflowScroll ? s.id : null;
  }

  /** Scroll a frame by (dx, dy), clamped to its content. Returns whether anything moved. */
  scrollBy(frameId: NodeId, dx: number, dy: number): boolean {
    const f = this.doc.get(frameId);
    if (!f?.overflowScroll) return false;
    const kids = this.doc.children(frameId).filter((c) => c.visible && !c.scrollFixed);
    const cur = this.scrollOffsets.get(frameId) ?? { x: 0, y: 0 };
    // content extent at zero scroll
    let right = 0,
      bottom = 0;
    for (const c of kids) {
      right = Math.max(right, c.x + cur.x + c.width);
      bottom = Math.max(bottom, c.y + cur.y + c.height);
    }
    right += f.paddingRight ?? 0;
    bottom += f.paddingBottom ?? 0;
    const canX = f.overflowScroll !== 'VERTICAL',
      canY = f.overflowScroll !== 'HORIZONTAL';
    const next = {
      x: canX ? Math.max(0, Math.min(Math.max(0, right - f.width), cur.x + dx)) : 0,
      y: canY ? Math.max(0, Math.min(Math.max(0, bottom - f.height), cur.y + dy)) : 0,
    };
    const ddx = next.x - cur.x,
      ddy = next.y - cur.y;
    if (!ddx && !ddy) return false;
    this.scrollOffsets.set(frameId, next);
    // shift content directly on the private copy (no layout pass, so auto layout keeps the shift)
    const ops: Op[] = kids.map((c) => ({ op: 'set', id: c.id, props: { x: c.x - ddx, y: c.y - ddy } }) as Op);
    // fixed layers stay above the content scrolling under them
    const all = this.doc.children(frameId);
    let top = all.length ? all[all.length - 1].index : 'a';
    for (const c of all) if (c.scrollFixed && c.id !== all[all.length - 1].id) ops.push({ op: 'set', id: c.id, props: { index: (top = top + 'V') } } as Op);
    this.doc.apply({ ops }, { remote: true });
    return true;
  }

  /** The node with reactions for `trigger` at a page point, searching from the deepest layer up to the screen. */
  hit(point: Vec2, trigger: Reaction['trigger']): NodeId | null {
    const screen = this.screen;
    const page = this.doc.pageOf(screen);
    if (!page) return null;
    const b = this.doc.worldBounds(screen);
    if (point.x < b.x || point.y < b.y || point.x > b.x + b.width || point.y > b.y + b.height) return null;
    const path = hitTestPath(this.doc, page.id, point, { includeLocked: true });
    const start = path.findIndex((n) => n.id === screen);
    if (start < 0) return null;
    for (let i = path.length - 1; i >= start; i--) if (path[i].reactions?.some((r) => r.trigger === trigger)) return path[i].id;
    return null;
  }

  /** Run the `trigger` reactions of a node (or of its nearest ancestor that has some). */
  fire(nodeId: NodeId, trigger: Reaction['trigger']): FireResult {
    let n: NodeData | undefined = this.doc.get(nodeId);
    while (n && !n.reactions?.some((r) => r.trigger === trigger)) {
      if (n.id === this.screen) return { events: [] };
      n = this.doc.get(n.parent);
    }
    if (!n) return { events: [] };
    const events: PlayerEvent[] = [];
    const reverts: (() => PlayerEvent[])[] = [];
    for (const r of n.reactions!.filter((x) => x.trigger === trigger)) {
      const res = this.run(r.action, n.id, trigger === 'ON_HOVER' || trigger === 'ON_PRESS');
      events.push(...res.events);
      if (res.revert) reverts.push(res.revert);
    }
    return { events, revert: reverts.length ? () => reverts.reverse().flatMap((f) => f()) : undefined };
  }

  /** AFTER_DELAY reactions anywhere on the screen (frames, and variants of interactive components). */
  delayed(): { nodeId: NodeId; delay: number }[] {
    const out: { nodeId: NodeId; delay: number }[] = [];
    if (!this.doc.has(this.screen)) return out;
    this.doc.walk(this.screen, (n) => {
      if (!n.visible) return false;
      const r = n.reactions?.find((x) => x.trigger === 'AFTER_DELAY');
      if (r) out.push({ nodeId: n.id, delay: r.delay ?? 800 });
    });
    return out;
  }

  run(a: PrototypeAction, sourceId: NodeId, temporary = false): FireResult {
    switch (a.type) {
      case 'NAVIGATE':
        if (!this.doc.has(a.destinationId)) return { events: [] };
        this.overlay = null;
        this.stack.push(a.destinationId);
        this.arrivals[this.stack.length - 1] = { transition: a.transition ?? 'INSTANT', duration: a.duration ?? 300 };
        // a new screen starts at the top
        this.scrollBy(a.destinationId, -1e9, -1e9);
        return { events: [{ type: 'navigate', id: a.destinationId, transition: a.transition ?? 'INSTANT', duration: a.duration ?? 300 }] };
      case 'BACK':
        if (this.overlay) {
          this.overlay = null;
          return { events: [{ type: 'overlay', id: null }] };
        }
        if (this.stack.length < 2) return { events: [] };
        const how = this.arrivals[this.stack.length - 1] ?? { transition: 'INSTANT', duration: 0 };
        this.arrivals.length = this.stack.length - 1;
        this.stack.pop();
        return { events: [{ type: 'navigate', id: this.current, transition: how.transition, duration: how.duration, back: true }] };
      case 'OPEN_URL':
        return { events: [{ type: 'url', url: a.url }] };
      case 'OVERLAY': {
        if (!this.doc.has(a.destinationId)) return { events: [] };
        const prev = this.overlay;
        this.overlay = a.destinationId;
        const revert = temporary
          ? () => {
              this.overlay = prev;
              return [{ type: 'overlay', id: prev } as PlayerEvent];
            }
          : undefined;
        return { events: [{ type: 'overlay', id: a.destinationId }], revert };
      }
      case 'CLOSE_OVERLAY':
        this.overlay = null;
        return { events: [{ type: 'overlay', id: null }] };
      case 'CHANGE_TO': {
        const inst = this.instanceFor(sourceId, a.destinationId);
        const target = this.doc.get(a.destinationId);
        if (!inst || target?.type !== 'COMPONENT' || inst.componentId === target.id) return { events: [] };
        const prev = inst.componentId!;
        swapInstance(this.editor, inst.id, target.id);
        const revert = temporary
          ? () => {
              if (!this.doc.has(inst.id) || !this.doc.has(prev)) return [];
              swapInstance(this.editor, inst.id, prev);
              return [{ type: 'change' } as PlayerEvent];
            }
          : undefined;
        return { events: [{ type: 'change' }], revert };
      }
      case 'SET_VARIABLE': {
        const v = this.doc.get(a.variableId);
        if (v?.type !== 'VARIABLE') return { events: [] };
        const cur = this.value(a.variableId);
        let next: VariableValue | undefined = a.value;
        if (a.toggle) next = !cur;
        else if (a.add !== undefined) next = (typeof cur === 'number' ? cur : 0) + a.add;
        if (next === undefined) return { events: [] };
        this.vars.set(a.variableId, next);
        this.editor.transact('Set variable', () => this.writeVar(a.variableId, next!));
        return { events: [{ type: 'change' }] };
      }
      case 'UPDATE_MEDIA_RUNTIME': {
        const id = this.videoFor(a.destinationId || sourceId);
        if (!id) return { events: [] };
        const s = { ...this.mediaState(id) };
        const seek: { seekTo?: number; seekBy?: number } = {};
        const act: MediaAction = a.mediaAction;
        if (act === 'PLAY') s.playing = true;
        else if (act === 'PAUSE') s.playing = false;
        else if (act === 'TOGGLE_PLAY_PAUSE') s.playing = !s.playing;
        else if (act === 'MUTE') s.muted = true;
        else if (act === 'UNMUTE') s.muted = false;
        else if (act === 'TOGGLE_MUTE_UNMUTE') s.muted = !s.muted;
        else if (act === 'SKIP_FORWARD') seek.seekBy = a.amountToSkip ?? 5;
        else if (act === 'SKIP_BACKWARD') seek.seekBy = -(a.amountToSkip ?? 5);
        else if (act === 'SKIP_TO') seek.seekTo = a.newTimestamp ?? 0;
        this.media.set(id, s);
        return { events: [{ type: 'media', id, ...s, ...seek }] };
      }
      case 'CONDITIONAL': {
        const cur = this.value(a.variableId);
        const branch = JSON.stringify(cur) === JSON.stringify(a.equals) ? a.then : (a.else ?? []);
        const events: PlayerEvent[] = [];
        for (const b of branch) events.push(...this.run(b, sourceId).events);
        return { events };
      }
    }
  }

  /** A video played from `from` to `to` seconds: runs its ON_MEDIA_HIT reactions for times in between. */
  mediaTime(id: NodeId, from: number, to: number): PlayerEvent[] {
    const out: PlayerEvent[] = [];
    for (const r of this.doc.get(id)?.reactions ?? []) {
      const t = r.mediaHitTime ?? 0;
      if (r.trigger === 'ON_MEDIA_HIT' && t > from && t <= to) out.push(...this.run(r.action, id).events);
    }
    return out;
  }

  /** A video finished (it doesn't loop): it stops, and its ON_MEDIA_END reactions run. */
  mediaEnded(id: NodeId): PlayerEvent[] {
    this.media.set(id, { ...this.mediaState(id), playing: false });
    return (this.doc.get(id)?.reactions ?? []).filter((r) => r.trigger === 'ON_MEDIA_END').flatMap((r) => this.run(r.action, id).events);
  }

  /** A video layer's playback now. */
  mediaState(id: NodeId): MediaState {
    const p = videoPlaybackOf(this.doc.get(id));
    return this.media.get(id) ?? { playing: p.autoplay, muted: p.muted };
  }

  /** The video a media action controls: the layer itself, else the first video inside it. */
  private videoFor(id: NodeId): NodeId | null {
    if (!this.doc.has(id)) return null;
    let found: NodeId | null = null;
    this.doc.walk(id, (n) => {
      if (found) return false;
      if (hasVideo(n)) found = n.id;
    });
    return found;
  }

  /** Current value of a variable as the prototype sees it. */
  value(variableId: NodeId): VariableValue | undefined {
    return this.vars.get(variableId) ?? resolveVariable(this.doc, variableId, this.screen);
  }

  /** The instance a CHANGE_TO applies to: the nearest one (self or ancestor) whose variants include the target. */
  private instanceFor(sourceId: NodeId, destinationId: NodeId): NodeData | undefined {
    const set = componentSetOf(this.doc, destinationId);
    let n = this.doc.get(sourceId);
    let nearest: NodeData | undefined;
    while (n) {
      if (n.type === 'INSTANCE' && n.componentId) {
        nearest ??= n;
        if (set && componentSetOf(this.doc, n.componentId)?.id === set.id) return n;
      }
      n = this.doc.get(n.parent);
    }
    return set ? undefined : nearest;
  }

  /** Make every mode of the variable hold `v`, so it applies whichever mode a frame uses. */
  private writeVar(id: NodeId, v: VariableValue) {
    const node = this.doc.get(id);
    if (!node) return;
    const coll = this.doc.get(node.parent);
    const modes = new Set([...Object.keys(node.valuesByMode ?? {}), ...(coll?.modes ?? []).map((m) => m.id)]);
    const valuesByMode: Record<string, VariableValue> = {};
    for (const m of modes) valuesByMode[m] = v;
    this.editor.update(id, { valuesByMode });
  }
}

// ---------------------------------------------------------------------------
// Flows
// ---------------------------------------------------------------------------

/** A page's flows (named starting frames), in the order they were added. Older files had one start. */
export function pageFlows(doc: Document, pageId: NodeId): FlowStart[] {
  const page = doc.get(pageId);
  if (!page) return [];
  const list = page.flowStartingPoints ?? (page.prototypeStartId ? [{ nodeId: page.prototypeStartId, name: 'Flow 1' }] : []);
  return list.filter((f) => doc.has(f.nodeId));
}

/** Page props that make `nodeId` start a flow (renaming it if it already does), or with `name: null` stop it. */
export function flowStartProps(doc: Document, pageId: NodeId, nodeId: NodeId, name: string | null, description?: string): NodeProps {
  const flows = pageFlows(doc, pageId);
  const i = flows.findIndex((f) => f.nodeId === nodeId);
  let next: FlowStart[];
  if (name === null) next = flows.filter((f) => f.nodeId !== nodeId);
  else {
    const taken = new Set(flows.map((f) => f.name));
    let k = flows.length + 1;
    while (taken.has(`Flow ${k}`)) k++;
    const flow: FlowStart = { nodeId, name: name.trim() || flows[i]?.name || `Flow ${k}`, ...((description ?? flows[i]?.description) ? { description: description ?? flows[i]?.description } : {}) };
    next = i >= 0 ? flows.map((f, j) => (j === i ? flow : f)) : [...flows, flow];
  }
  return { flowStartingPoints: next.length ? next : undefined, prototypeStartId: undefined };
}

// ---------------------------------------------------------------------------
// Smart animate
// ---------------------------------------------------------------------------

const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Layers of a screen keyed by their name path below it (how smart animate matches layers). */
function namePaths(doc: Document, rootId: NodeId): Map<string, NodeData> {
  const out = new Map<string, NodeData>();
  const walk = (id: NodeId, prefix: string) => {
    const seen = new Map<string, number>();
    for (const c of doc.children(id)) {
      // same-named siblings match in order
      const k = seen.get(c.name) ?? 0;
      seen.set(c.name, k + 1);
      const path = `${prefix}/${c.name}#${k}`;
      out.set(path, c);
      walk(c.id, path);
    }
  };
  walk(rootId, '');
  return out;
}

function solidColor(p?: Paint) {
  return p && p.type === 'SOLID' ? p.color : null;
}

/**
 * Smart animate from one screen to another: a private copy of the document in which the target
 * screen's layers start where their same-named counterparts were and move to their own place.
 * New layers fade in and layers only on the old screen fade out. Call at(t) with t from 0 to 1, then draw `doc`.
 */
export function smartAnimate(source: Document, fromId: NodeId, toId: NodeId): { doc: Document; at(t: number): void } {
  const doc = Document.fromJSON(structuredClone(source.toJSON()));
  const A = namePaths(source, fromId);
  const B = namePaths(source, toId);
  const fromRoot = source.must(fromId),
    toRoot = source.must(toId);
  type Track = { id: NodeId; from: Partial<NodeData>; to: Partial<NodeData> };
  const tracks: Track[] = [];
  const pick = (n: NodeData): Partial<NodeData> => ({ x: n.x, y: n.y, width: n.width, height: n.height, rotation: n.rotation, opacity: n.opacity, cornerRadius: n.cornerRadius, fills: n.fills });
  // the screens themselves morph size and background
  tracks.push({ id: toId, from: { width: fromRoot.width, height: fromRoot.height, fills: fromRoot.fills, cornerRadius: fromRoot.cornerRadius }, to: { width: toRoot.width, height: toRoot.height, fills: toRoot.fills, cornerRadius: toRoot.cornerRadius } });
  for (const [path, b] of B) {
    const a = A.get(path);
    if (a) tracks.push({ id: b.id, from: pick(a), to: pick(b) });
    else tracks.push({ id: b.id, from: { opacity: 0 }, to: { opacity: b.opacity } });
  }
  // layers only on the old screen fade out where they were: copied into the new one for the tween
  const parentPath = (path: string) => path.slice(0, path.lastIndexOf('/'));
  for (const [path, a] of A) {
    if (B.has(path) || (parentPath(path) && !B.has(parentPath(path)) && A.has(parentPath(path)))) continue;
    const host = B.get(parentPath(path));
    let x = a.x,
      y = a.y;
    if (!host) for (let p = source.get(a.parent); p && p.id !== fromId; p = source.get(p.parent)) (x += p.x), (y += p.y);
    const parentId = host?.id ?? toId;
    // keep its stacking: just above the nearest earlier sibling that's on both screens
    const sibs = source.children(a.parent!);
    let after: NodeData | undefined;
    for (const sib of sibs.slice(0, sibs.indexOf(a)).reverse()) {
      const sp = [...A].find(([, n]) => n.id === sib.id)?.[0];
      after = sp ? B.get(sp) : undefined;
      if (after) break;
    }
    const kids = doc.children(parentId);
    const next = after ? kids[kids.findIndex((k) => k.id === after!.id) + 1] : kids[0];
    const index = indexBetween(after?.index ?? null, next?.index ?? null);
    const copy = (n: NodeData, parent: NodeId, top: boolean) => {
      const id = `${n.id}~out`;
      if (doc.has(id)) return;
      doc.apply({ ops: [{ op: 'create', node: { ...structuredClone(n), id, parent, ...(top ? { x, y, index, layoutPositioning: 'ABSOLUTE' as const, reactions: [] } : {}) } }] }, { remote: true });
      for (const c of source.children(n.id)) copy(c, id, false);
    };
    copy(a, parentId, true);
    tracks.push({ id: `${a.id}~out`, from: { opacity: a.opacity }, to: { opacity: 0 } });
  }
  const blend = (from: Partial<NodeData>, to: Partial<NodeData>, t: number): Partial<NodeData> => {
    const out: Partial<NodeData> = {};
    for (const k of ['x', 'y', 'width', 'height', 'rotation', 'opacity'] as const) if (from[k] !== undefined && to[k] !== undefined) (out as Record<string, number>)[k] = lerp(from[k] as number, to[k] as number, t);
    if (typeof from.cornerRadius === 'number' && typeof to.cornerRadius === 'number') out.cornerRadius = lerp(from.cornerRadius, to.cornerRadius, t);
    const ca = solidColor(from.fills?.[0]),
      cb = solidColor(to.fills?.[0]);
    if (ca && cb && to.fills) out.fills = [{ ...(to.fills[0] as Paint & { type: 'SOLID' }), color: { r: lerp(ca.r, cb.r, t), g: lerp(ca.g, cb.g, t), b: lerp(ca.b, cb.b, t), a: lerp(ca.a, cb.a, t) } }, ...to.fills.slice(1)];
    return out;
  };
  return {
    doc,
    at(t: number) {
      const e = ease(Math.max(0, Math.min(1, t)));
      doc.apply({ ops: tracks.filter((tr) => doc.has(tr.id)).map((tr) => ({ op: 'set', id: tr.id, props: blend(tr.from, tr.to, e) }) as Op) }, { remote: true });
    },
  };
}
